// KundenverbindungMergePage.tsx — Merge-Seite für Kundenverbindungen (KVs).
// Eigene Route `/contactgroups/merge?clusterIds=…` (analog `/contacts/merge`).
// Optik/Struktur 1:1 aus `ContactMergePage` adaptiert: Sieger-Wahl
// (.candidateCard/„Als Sieger wählen"), editierbarer Sieger-Name, wählbare
// Verantwortlichkeit (AP) DIREKT NACH DEM NAMEN, Mitglieder nebeneinander
// (Spalte je KV) mit Auto-Check-Dedupe (Sieger gewinnt Duplikate), manuelles
// Abwählen, Submit → POST contactgroup/merge.
//
// Datenquellen (SP2, in dev):
//   GET contactgroup/kundenverbindungen → { id, name, memberCount,
//        ansprechpartnerId, ansprechpartnerName }[]  (tenant-scoped)
//   GET contactgroup/:id/contacts        → { id, name, typ }[]
//   POST contactgroup/merge { winnerGroupId, loserGroupIds, finalName?,
//        finalAnsprechpartnerId?, selectedMemberContactIds }

import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { makeStyles, mergeClasses, shorthands } from "@fluentui/react-components";
import {
  ArrowLeft20Regular,
  Save20Regular,
  CheckmarkCircle20Filled,
  Warning20Regular,
  PeopleSwap20Regular,
  Edit16Regular,
  ArrowUndo16Regular,
} from "@fluentui/react-icons";
import { BRAND } from "../components/risikoanalyseStep/tokens";
import { apiFetch } from "../utils/apiFetch";
import { contactTypeLabel } from "../utils/contactTypeLabel";

/** Rücksprungziel nach erfolgreichem Merge: die Karte „Kundenverbindungen" im
 *  Dashboard (seit 28.09.2026; vorher /contacts?view=kundenverbindungen) — sie
 *  lädt beim Mount frisch, sodass umbenannte Sieger-KV / verschwundene
 *  Verlierer sofort sichtbar sind. */
const BACK_ROUTE = "/?kv=1";

const useStyles = makeStyles({
  root: {
    paddingTop: "24px",
    paddingBottom: "48px",
    paddingLeft: "32px",
    paddingRight: "32px",
    fontFamily: BRAND.fontPrimary,
    color: BRAND.ink,
    maxWidth: "1280px",
    marginLeft: "auto",
    marginRight: "auto",
    display: "flex",
    flexDirection: "column",
    rowGap: "20px",
  },
  backBtn: {
    alignSelf: "flex-start",
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    ...shorthands.padding("6px", "12px"),
    backgroundColor: "transparent",
    ...shorthands.border("1px", "solid", "transparent"),
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    cursor: "pointer",
    ...shorthands.borderRadius("6px"),
    ":hover": { backgroundColor: BRAND.bg },
  },
  bar: {
    backgroundColor: BRAND.purple,
    backgroundImage: `linear-gradient(135deg, ${BRAND.purple} 0%, ${BRAND.ink} 100%)`,
    color: BRAND.white,
    paddingTop: "20px",
    paddingBottom: "20px",
    paddingLeft: "24px",
    paddingRight: "24px",
    ...shorthands.borderRadius("10px"),
    boxShadow: "0 12px 24px rgba(45,20,43,0.18)",
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
  },
  titleRow: { display: "flex", alignItems: "center", columnGap: "12px" },
  titleIcon: {
    width: "40px",
    height: "40px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255,255,255,0.1)",
    color: BRAND.gold,
    ...shorthands.borderRadius("999px"),
  },
  title: {
    margin: 0,
    fontFamily: BRAND.fontAccent,
    fontSize: "clamp(1.4rem, 3.4vw, 1.9rem)",
    color: BRAND.gold,
    flexGrow: 1,
  },
  goldLine: {
    height: "3px",
    backgroundColor: BRAND.gold,
    ...shorthands.borderRadius("999px"),
    width: "60px",
  },
  subtitle: { color: "rgba(255,255,255,0.85)", fontSize: "0.9rem" },
  body: {
    backgroundColor: BRAND.white,
    paddingTop: "20px",
    paddingBottom: "20px",
    paddingLeft: "20px",
    paddingRight: "20px",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("10px"),
    display: "flex",
    flexDirection: "column",
    rowGap: "18px",
  },
  sectionLabel: {
    fontSize: "0.72rem",
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#5d5664",
    marginBottom: "8px",
  },
  winnerRow: {
    display: "flex",
    columnGap: "12px",
    rowGap: "12px",
    flexWrap: "wrap",
  },
  candidateCard: {
    flexGrow: 1,
    flexBasis: "240px",
    backgroundColor: BRAND.white,
    color: BRAND.ink,
    paddingTop: "12px",
    paddingBottom: "12px",
    paddingLeft: "14px",
    paddingRight: "14px",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("10px"),
    display: "flex",
    flexDirection: "column",
    rowGap: "6px",
  },
  candidateActive: {
    backgroundColor: BRAND.purple,
    color: BRAND.white,
    ...shorthands.borderColor(BRAND.ink),
    boxShadow: `0 0 0 3px ${BRAND.purple}33`,
  },
  winnerName: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1.05rem",
    color: BRAND.ink,
  },
  winnerSub: { color: BRAND.muted, fontSize: "0.82rem" },
  apLine: { color: BRAND.ink, fontSize: "0.84rem" },
  // Weiß-Varianten für die lila Sieger-Karte. MÜSSEN nach den überschriebenen
  // Klassen stehen: Griffel-Atomklassen entscheiden nach Definitionsreihenfolge,
  // nicht nach der Reihenfolge im className. Zusätzlich per mergeClasses gesetzt.
  textWhite: { color: BRAND.white },
  textWhiteSoft: { color: "rgba(255,255,255,0.82)" },
  survivorPick: {
    alignSelf: "flex-start",
    appearance: "none",
    cursor: "pointer",
    backgroundColor: BRAND.white,
    color: BRAND.purple,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.8rem",
    fontWeight: 600,
    paddingTop: "5px",
    paddingBottom: "5px",
    paddingLeft: "10px",
    paddingRight: "10px",
    ...shorthands.border("1px", "solid", BRAND.purple),
    ...shorthands.borderRadius("6px"),
    ":hover": { backgroundColor: "#f5eef6" },
    ":disabled": { opacity: 0.45, cursor: "not-allowed" },
  },
  survivorPickActive: {
    backgroundColor: BRAND.white,
    color: BRAND.purple,
    ...shorthands.borderColor(BRAND.white),
    ":hover": { backgroundColor: "rgba(255,255,255,0.85)" },
  },
  // Editierbarer Sieger-Name (eine Zeile: Wert + Stift/Reset bzw. Inline-Input).
  nameRow: {
    display: "flex",
    alignItems: "center",
    columnGap: "8px",
    flexWrap: "wrap",
  },
  nameValue: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1.1rem",
    color: BRAND.ink,
  },
  nameInput: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.95rem",
    paddingTop: "7px",
    paddingBottom: "7px",
    paddingLeft: "10px",
    paddingRight: "10px",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("8px"),
    minWidth: "280px",
  },
  iconBtn: {
    appearance: "none",
    border: "none",
    background: "transparent",
    cursor: "pointer",
    ...shorthands.padding("2px"),
    display: "inline-flex",
    alignItems: "center",
    color: BRAND.purple,
  },
  apSelect: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "10px",
    paddingRight: "10px",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("8px"),
    backgroundColor: BRAND.white,
    color: BRAND.ink,
    minWidth: "300px",
  },
  tableScroll: { overflowX: "auto" },
  compareGrid: {
    display: "grid",
    columnGap: "8px",
    minWidth: "fit-content",
  },
  compareCol: {
    display: "flex",
    flexDirection: "column",
    rowGap: "4px",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("8px"),
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "10px",
    paddingRight: "10px",
    backgroundColor: BRAND.bg,
  },
  compareColWinner: {
    ...shorthands.borderColor(BRAND.purple),
    backgroundColor: "#f5eef6",
  },
  compareColHead: {
    fontFamily: BRAND.fontAccent,
    fontSize: "0.9rem",
    color: BRAND.ink,
    paddingBottom: "4px",
    ...shorthands.borderBottom("1px", "solid", BRAND.borderDashed),
    marginBottom: "2px",
  },
  compareColHeadWinner: { color: BRAND.purple },
  compareColSub: { color: BRAND.muted, fontSize: "0.74rem" },
  selItemRow: {
    display: "flex",
    alignItems: "flex-start",
    columnGap: "6px",
    fontSize: "0.84rem",
    color: BRAND.ink,
  },
  selItemDropped: { opacity: 0.45, textDecorationLine: "line-through" },
  selItemCheckbox: { marginTop: "3px", flexShrink: 0, cursor: "pointer" },
  dupHint: {
    marginLeft: "6px",
    fontSize: "0.72rem",
    color: BRAND.muted,
    fontStyle: "italic",
    textDecorationLine: "none",
    display: "inline-block",
  },
  recordEmpty: { fontSize: "0.84rem", color: BRAND.muted, fontStyle: "italic" },
  hint: { color: BRAND.muted, fontSize: "0.82rem", marginBottom: "8px" },
  footerWrap: { display: "flex", flexDirection: "column", rowGap: "12px" },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    columnGap: "10px",
    flexWrap: "wrap",
  },
  footerMsgRow: {
    display: "flex",
    alignItems: "center",
    columnGap: "10px",
    flexWrap: "wrap",
  },
  primaryBtn: {
    appearance: "none",
    cursor: "pointer",
    backgroundColor: BRAND.purple,
    backgroundImage: "none",
    color: BRAND.white,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.95rem",
    fontWeight: 600,
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "20px",
    paddingRight: "20px",
    ...shorthands.border("1px", "solid", BRAND.purple),
    ...shorthands.borderRadius("8px"),
    display: "inline-flex",
    alignItems: "center",
    columnGap: "8px",
    ":hover": { backgroundColor: BRAND.ink },
    ":disabled": { opacity: 0.5, cursor: "not-allowed" },
  },
  secondaryBtn: {
    appearance: "none",
    cursor: "pointer",
    backgroundColor: BRAND.white,
    backgroundImage: "none",
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.95rem",
    paddingTop: "9px",
    paddingBottom: "9px",
    paddingLeft: "16px",
    paddingRight: "16px",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("8px"),
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    ":disabled": { opacity: 0.5, cursor: "not-allowed" },
  },
  errorBox: {
    backgroundColor: "#fdecec",
    color: "#8b1c1c",
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "14px",
    paddingRight: "14px",
    ...shorthands.border("1px", "solid", "#f5b3b3"),
    ...shorthands.borderRadius("8px"),
    display: "inline-flex",
    alignItems: "center",
    columnGap: "8px",
  },
  successBox: {
    backgroundColor: "#e6f6ec",
    color: "#0e7c3a",
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "14px",
    paddingRight: "14px",
    ...shorthands.border("1px", "solid", "#b6dfc4"),
    ...shorthands.borderRadius("8px"),
    display: "inline-flex",
    alignItems: "center",
    columnGap: "8px",
  },
});

/** KV-Stammdaten aus `contactgroup/kundenverbindungen` (auf clusterIds gefiltert). */
interface KvSummary {
  id: number;
  name: string;
  memberCount: number;
  ansprechpartnerId: number | null;
  ansprechpartnerName: string | null;
}
/** Mitglied einer KV aus `contactgroup/:id/contacts`. */
interface KvMember {
  id: number;
  name: string;
  typ: string;
}
/** Pro KV: Stammdaten + geladene Mitglieder. */
interface KvDetail extends KvSummary {
  members: KvMember[];
}

const KundenverbindungMergePage = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  // clusterIds (CSV) → number[], dedupliziert, in Eingabe-Reihenfolge.
  const clusterIds = useMemo<number[]>(() => {
    const raw = params.get("clusterIds") ?? "";
    const seen = new Set<number>();
    const out: number[] = [];
    for (const s of raw.split(",")) {
      const n = Number(s.trim());
      if (Number.isFinite(n) && n > 0 && !seen.has(n)) {
        seen.add(n);
        out.push(n);
      }
    }
    return out;
  }, [params]);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Geladene KV-Details (Stammdaten + Mitglieder), Schlüssel = kvId.
  const [details, setDetails] = useState<Record<number, KvDetail>>({});
  const [kvOrder, setKvOrder] = useState<number[]>([]);

  const [survivorKvId, setSurvivorKvId] = useState<number | null>(null);
  // Editierbarer Sieger-Name (vorbelegt mit Sieger-Name, bei Sieger-Wechsel reset).
  const [nameEdit, setNameEdit] = useState("");
  const [editingName, setEditingName] = useState(false);
  // Gewählte Verantwortlichkeit (ansprechpartnerId) — null = „— keine".
  const [apEdit, setApEdit] = useState<number | null>(null);
  // Abgewählte Mitglieder je KV-Spalte (checked = NICHT in dropped[kvId]).
  const [dropped, setDropped] = useState<Record<number, Set<number>>>({});

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  /** Auto-Check-Dedupe: Sieger zuerst → Sieger gewinnt jede doppelte contactId.
   *  Jede contactId, die in einer früheren Spalte schon „vergeben" wurde, wird in
   *  weiteren Spalten abgewählt (in `dropped`). Wird beim Laden UND bei jedem
   *  Sieger-Wechsel neu berechnet (manuelle Abwahl wird dabei verworfen). */
  const computeDedupe = (
    detailMap: Record<number, KvDetail>,
    survivor: number,
    order: number[],
  ): Record<number, Set<number>> => {
    const seen = new Set<number>();
    const next: Record<number, Set<number>> = {};
    const ordered = [survivor, ...order.filter((id) => id !== survivor)];
    for (const kvId of ordered) {
      next[kvId] = new Set<number>();
      for (const m of detailMap[kvId]?.members ?? []) {
        if (seen.has(m.id)) next[kvId].add(m.id);
        else seen.add(m.id);
      }
    }
    return next;
  };

  useEffect(() => {
    let aborted = false;
    const run = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        // KV-Liste holen, auf clusterIds filtern → Name + AP je KV.
        const { data: list } = await apiFetch<KvSummary[]>(
          "contactgroup/kundenverbindungen",
        );
        const byId = new Map<number, KvSummary>();
        for (const kv of Array.isArray(list) ? list : []) byId.set(kv.id, kv);
        // Nur die angeforderten KVs, in clusterIds-Reihenfolge.
        const summaries = clusterIds
          .map((id) => byId.get(id))
          .filter((v): v is KvSummary => !!v);
        // Mitglieder je KV laden.
        const memberEntries = await Promise.all(
          summaries.map((kv) =>
            apiFetch<KvMember[]>(`contactgroup/${kv.id}/contacts`)
              .then(({ data }) => [kv.id, Array.isArray(data) ? data : []] as const)
              .catch(() => [kv.id, [] as KvMember[]] as const),
          ),
        );
        if (aborted) return;
        const membersById = new Map(memberEntries);
        const detailMap: Record<number, KvDetail> = {};
        for (const kv of summaries) {
          detailMap[kv.id] = { ...kv, members: membersById.get(kv.id) ?? [] };
        }
        const order = summaries.map((kv) => kv.id);
        // Default-Sieger: KV mit den meisten Mitgliedern (Fallback: erste).
        let survivor = order[0] ?? null;
        let bestCount = -1;
        for (const kv of summaries) {
          if (kv.memberCount > bestCount) {
            bestCount = kv.memberCount;
            survivor = kv.id;
          }
        }
        setDetails(detailMap);
        setKvOrder(order);
        setSurvivorKvId(survivor);
        if (survivor != null) {
          setNameEdit(detailMap[survivor]?.name ?? "");
          setApEdit(detailMap[survivor]?.ansprechpartnerId ?? null);
          setDropped(computeDedupe(detailMap, survivor, order));
        }
      } catch (e: any) {
        if (!aborted) setLoadError(String(e?.message ?? e));
      } finally {
        if (!aborted) setLoading(false);
      }
    };
    run();
    return () => {
      aborted = true;
    };
  }, [clusterIds]);

  // Sieger wechseln: Name + AP auf neuen Sieger zurücksetzen, Dedupe neu rechnen.
  const pickSurvivor = (kvId: number) => {
    setSurvivorKvId(kvId);
    setNameEdit(details[kvId]?.name ?? "");
    setApEdit(details[kvId]?.ansprechpartnerId ?? null);
    setEditingName(false);
    setDropped(computeDedupe(details, kvId, kvOrder));
  };

  const toggleDropped = (kvId: number, contactId: number) =>
    setDropped((prev) => {
      const next = new Set(prev[kvId] ?? []);
      if (next.has(contactId)) next.delete(contactId);
      else next.add(contactId);
      return { ...prev, [kvId]: next };
    });

  // Distinkte Verantwortlichkeiten (AP) über alle Kandidaten-KVs (+ „— keine").
  const apOptions = useMemo<{ id: number; name: string }[]>(() => {
    const seen = new Map<number, string>();
    for (const id of kvOrder) {
      const d = details[id];
      if (d?.ansprechpartnerId != null && !seen.has(d.ansprechpartnerId)) {
        seen.set(d.ansprechpartnerId, d.ansprechpartnerName ?? `#${d.ansprechpartnerId}`);
      }
    }
    return Array.from(seen, ([id, name]) => ({ id, name }));
  }, [details, kvOrder]);

  // Spalten-Reihenfolge: Sieger zuerst, dann übrige in Eingabe-Reihenfolge.
  const columnOrder = useMemo<number[]>(() => {
    if (survivorKvId == null) return kvOrder;
    return [survivorKvId, ...kvOrder.filter((id) => id !== survivorKvId)];
  }, [survivorKvId, kvOrder]);

  const onMerge = async () => {
    if (survivorKvId == null || saving || done) return;
    const loserKvIds = kvOrder.filter((id) => id !== survivorKvId);
    if (loserKvIds.length === 0) return;
    // Finale, deduplizierte Mitgliederliste: alle gecheckten Zellen über ALLE
    // Spalten (checked = NICHT dropped). Set entfernt verbleibende Duplikate.
    const memberIds = new Set<number>();
    for (const kvId of columnOrder) {
      const drop = dropped[kvId] ?? new Set<number>();
      for (const m of details[kvId]?.members ?? []) {
        if (!drop.has(m.id)) memberIds.add(m.id);
      }
    }
    setSaving(true);
    setError(null);
    try {
      await apiFetch("contactgroup/merge", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          winnerGroupId: survivorKvId,
          loserGroupIds: loserKvIds,
          finalName: nameEdit.trim() || undefined,
          finalAnsprechpartnerId: apEdit ?? undefined,
          selectedMemberContactIds: [...memberIds],
        }),
      });
      setDone(true);
      setTimeout(() => navigate(BACK_ROUTE, { replace: true }), 1200);
    } catch (e: any) {
      setError(String(e?.message ?? e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.root}>
        <p style={{ color: BRAND.muted }}>Kundenverbindungen werden geladen…</p>
      </div>
    );
  }

  const kvList = columnOrder
    .map((id) => details[id])
    .filter((v): v is KvDetail => !!v);

  if (kvList.length < 2) {
    return (
      <div className={styles.root}>
        <button className={styles.backBtn} onClick={() => navigate(-1)}>
          <ArrowLeft20Regular /> Zurück
        </button>
        <p>
          {loadError ??
            "Es konnten nicht genügend Kundenverbindungen geladen werden."}
        </p>
      </div>
    );
  }

  const survivor = survivorKvId != null ? details[survivorKvId] : null;
  const compareCols = `repeat(${kvList.length}, minmax(220px, 1fr))`;
  const canMerge = !saving && !done && survivorKvId != null && kvList.length >= 2;

  // Eine Mitglieder-Zelle (Checkbox + Name). Checked = behalten.
  const memberRow = (kvId: number, m: KvMember, isDup: boolean): ReactNode => {
    const isDropped = (dropped[kvId] ?? new Set<number>()).has(m.id);
    return (
      <label
        key={m.id}
        className={mergeClasses(styles.selItemRow, isDropped && styles.selItemDropped)}
      >
        <input
          type="checkbox"
          className={styles.selItemCheckbox}
          checked={!isDropped}
          disabled={done}
          onChange={() => toggleDropped(kvId, m.id)}
        />
        <span>
          {m.name}
          {m.typ ? <span className={styles.compareColSub}> · {contactTypeLabel(m.typ)}</span> : null}
          {isDup ? (
            <span className={styles.dupHint}>bereits in Sieger</span>
          ) : null}
        </span>
      </label>
    );
  };

  return (
    <div className={styles.root}>
      <button className={styles.backBtn} onClick={() => navigate(-1)}>
        <ArrowLeft20Regular /> Zurück
      </button>

      <div className={styles.bar}>
        <div className={styles.titleRow}>
          <span className={styles.titleIcon}>
            <PeopleSwap20Regular style={{ fontSize: 22 }} />
          </span>
          <h1 className={styles.title}>Kundenverbindungen zusammenführen</h1>
        </div>
        <div className={styles.goldLine} />
        <span className={styles.subtitle}>
          Wähle die führende Kundenverbindung (Sieger), passe Name &amp;
          Verantwortlichkeit an und entscheide, welche Mitglieder übernommen
          werden. Mitglieder, die in mehreren Verbindungen vorkommen, sind
          automatisch nur in der Sieger-Spalte angehakt; die Verlierer-
          Verbindungen werden nach dem Zusammenführen aufgelöst.
        </span>
      </div>

      <div className={styles.body}>
        {/* Sieger-Wahl: Karten nebeneinander, „Als Sieger wählen". */}
        <div>
          <div className={styles.sectionLabel}>Kundenverbindungen</div>
          <div className={styles.winnerRow}>
            {kvList.map((kv) => {
              const won = survivorKvId === kv.id;
              return (
                <div
                  key={kv.id}
                  className={mergeClasses(styles.candidateCard, won && styles.candidateActive)}
                >
                  <span
                    className={mergeClasses(styles.winnerName, won && styles.textWhite)}
                  >
                    {kv.name}
                  </span>
                  <span
                    className={mergeClasses(styles.winnerSub, won && styles.textWhiteSoft)}
                  >
                    {kv.memberCount} Kontakt{kv.memberCount === 1 ? "" : "e"}
                  </span>
                  <span
                    className={mergeClasses(styles.apLine, won && styles.textWhiteSoft)}
                  >
                    Verantw.: {kv.ansprechpartnerName ?? "— ohne"}
                  </span>
                  <button
                    type="button"
                    className={mergeClasses(styles.survivorPick, won && styles.survivorPickActive)}
                    disabled={done}
                    onClick={() => pickSurvivor(kv.id)}
                  >
                    {won ? "★ Sieger" : "Als Sieger wählen"}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Sieger-Name (editierbar) + Verantwortlichkeit DIREKT NACH DEM NAMEN. */}
        <div>
          <div className={styles.sectionLabel}>Name des Siegers</div>
          <div className={styles.nameRow}>
            {editingName ? (
              <>
                <input
                  autoFocus
                  type="text"
                  className={styles.nameInput}
                  value={nameEdit}
                  disabled={done}
                  onChange={(e) => setNameEdit(e.target.value)}
                  placeholder="Name der Kundenverbindung"
                />
                <button
                  type="button"
                  className={styles.iconBtn}
                  title="Fertig"
                  onClick={() => setEditingName(false)}
                >
                  <CheckmarkCircle20Filled />
                </button>
              </>
            ) : (
              <>
                <span className={styles.nameValue}>
                  {nameEdit.trim() || survivor?.name || "—"}
                </span>
                <button
                  type="button"
                  className={styles.iconBtn}
                  title="Namen bearbeiten"
                  disabled={done}
                  onClick={() => setEditingName(true)}
                >
                  <Edit16Regular />
                </button>
                {survivor && nameEdit.trim() !== survivor.name && (
                  <button
                    type="button"
                    className={styles.iconBtn}
                    title="Auf Sieger-Namen zurücksetzen"
                    disabled={done}
                    onClick={() => setNameEdit(survivor.name)}
                  >
                    <ArrowUndo16Regular />
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        <div>
          <div className={styles.sectionLabel}>Verantwortlichkeit</div>
          <div className={styles.hint}>
            Verantwortlichkeit (Ansprechpartner) des Siegers nach dem
            Zusammenführen. Standard ist die Verantwortlichkeit der Sieger-
            Verbindung; jede Kandidaten-Verantwortlichkeit ist wählbar.
          </div>
          <select
            className={styles.apSelect}
            value={apEdit == null ? "" : String(apEdit)}
            disabled={done}
            onChange={(e) =>
              setApEdit(e.target.value === "" ? null : Number(e.target.value))
            }
          >
            <option value="">— keine —</option>
            {apOptions.map((o) => (
              <option key={o.id} value={String(o.id)}>
                {o.name}
              </option>
            ))}
          </select>
        </div>

        {/* Mitglieder nebeneinander (Spalte je KV) + Auto-Check-Dedupe. */}
        <div>
          <div className={styles.sectionLabel}>Mitglieder</div>
          <div className={styles.hint}>
            Angehakte Mitglieder landen in der Sieger-Verbindung. Doppelte
            Mitglieder (gleicher Kontakt in mehreren Verbindungen) sind
            automatisch nur einmal angehakt. Einzelne Mitglieder lassen sich
            ab- oder wieder anwählen.
          </div>
          <div className={styles.tableScroll}>
            <div
              className={styles.compareGrid}
              style={{ gridTemplateColumns: compareCols }}
            >
              {kvList.map((kv) => {
                const won = survivorKvId === kv.id;
                const drop = dropped[kv.id] ?? new Set<number>();
                return (
                  <div
                    key={kv.id}
                    className={mergeClasses(styles.compareCol, won && styles.compareColWinner)}
                  >
                    <div
                      className={mergeClasses(styles.compareColHead, won && styles.compareColHeadWinner)}
                    >
                      {kv.name}
                      {won ? " · Sieger" : ""}
                    </div>
                    {kv.members.length === 0 ? (
                      <div className={styles.recordEmpty}>— keine Mitglieder</div>
                    ) : (
                      kv.members.map((m) =>
                        // „Duplikat"-Hinweis nur in Verlierer-Spalten, wenn die
                        // contactId dort durch das Auto-Dedupe abgewählt wurde.
                        memberRow(kv.id, m, !won && drop.has(m.id)),
                      )
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div className={styles.footerWrap}>
        <div className={styles.footer}>
          <button
            className={styles.secondaryBtn}
            onClick={() => navigate(-1)}
            disabled={saving}
          >
            <ArrowLeft20Regular /> Abbrechen
          </button>
          <button
            type="button"
            className={styles.primaryBtn}
            disabled={!canMerge}
            onClick={onMerge}
          >
            <Save20Regular />
            {saving
              ? "Zusammenführen…"
              : `Jetzt zusammenführen (${Math.max(kvList.length - 1, 0)} → Sieger)`}
          </button>
        </div>
        {(error || done || loadError) && (
          <div className={styles.footerMsgRow}>
            {(error || loadError) && (
              <div className={styles.errorBox}>
                <Warning20Regular /> {error ?? loadError}
              </div>
            )}
            {done && (
              <>
                <div className={styles.successBox}>
                  <CheckmarkCircle20Filled /> Zusammenführung erfolgreich. Du
                  wirst zurückgeleitet…
                </div>
                <button
                  type="button"
                  className={styles.secondaryBtn}
                  onClick={() => navigate(BACK_ROUTE, { replace: true })}
                >
                  <ArrowLeft20Regular /> Zurück
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default KundenverbindungMergePage;
